import { z } from 'zod'
import type { Role } from '@/features/auth/types'
import type { ManagedUser } from './api'

export const ROLES = [
  'super_admin',
  'admin',
  'teacher',
  'accountant',
  'librarian',
  'receptionist',
  'student',
  'parent',
] as const satisfies readonly Role[]

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters.`)
    .transform((v) => (v === '' ? null : v))

/** Password is required when creating an account and optional ("keep current") when editing. */
export function userSchema(mode: 'create' | 'edit') {
  return z.object({
    name: z.string().trim().min(1, 'Enter the person’s name.').max(150, 'Use at most 150 characters.'),
    email: z.email('Enter a valid email address.').max(180),
    role: z.enum(ROLES, { error: 'Choose a role.' }),
    phone: optionalText(30),
    password: z
      .string()
      .refine(
        (v) => (mode === 'edit' && v === '') || v.length >= 8,
        mode === 'create'
          ? 'Enter a password of at least 8 characters.'
          : 'Use at least 8 characters, or leave blank.',
      )
      .refine((v) => v.length <= 100, 'Use at most 100 characters.')
      .transform((v) => (v === '' ? null : v)),
    isActive: z.boolean(),
  })
}

export type UserFormInput = z.input<ReturnType<typeof userSchema>>

export function toUserFormValues(user: ManagedUser | null): UserFormInput {
  return {
    name: user?.name ?? '',
    email: user?.email ?? '',
    role: user?.role ?? 'teacher',
    phone: user?.phone ?? '',
    password: '',
    isActive: user?.isActive ?? true,
  }
}
